/**
 * RoleSwitcher Component
 *
 * Allows users with multiple roles to switch between them.
 * Displays a dropdown with all available roles and highlights the active one.
 */

import { useAllRoles, useActiveRole, useSwitchRole } from "@/hooks/use-role";
import { useUser } from "@/hooks/use-user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, User, Wrench, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

const roleIcons = {
  client: User,
  cleaner: Wrench,
  admin: Shield,
};

const roleColors = {
  client: "bg-blue-100 text-blue-800",
  cleaner: "bg-green-100 text-green-800",
  admin: "bg-purple-100 text-purple-800",
};

export function RoleSwitcher() {
  const { t } = useTranslation();
  const { isLoading: isLoadingUser } = useUser();
  const allRoles = useAllRoles();
  const activeRole = useActiveRole();
  const { switchRole, isLoading } = useSwitchRole();

  // Debug logging
  console.log('[RoleSwitcher] isLoadingUser:', isLoadingUser);
  console.log('[RoleSwitcher] allRoles:', allRoles);
  console.log('[RoleSwitcher] activeRole:', activeRole);
  console.log('[RoleSwitcher] allRoles.length:', allRoles.length);

  // Don't show while user data is loading
  if (isLoadingUser) {
    console.log('[RoleSwitcher] Not showing - user data is loading');
    return null;
  }

  // Don't show the switcher if user has only one role
  if (allRoles.length <= 1) {
    console.log('[RoleSwitcher] Not showing - user has <= 1 role');
    return null;
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      client: t("roles.client", "Client"),
      cleaner: t("roles.cleaner", "Cleaner"),
      admin: t("roles.admin", "Admin"),
    };
    return labels[role] || role;
  };

  const ActiveRoleIcon = activeRole ? roleIcons[activeRole as keyof typeof roleIcons] : User;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          disabled={isLoading}
        >
          {ActiveRoleIcon && <ActiveRoleIcon className="h-4 w-4" />}
          <span className="hidden sm:inline">
            {activeRole ? getRoleLabel(activeRole) : t("roles.selectRole", "Select Role")}
          </span>
          <Badge variant="secondary" className="ml-1 hidden sm:inline">
            {allRoles.length}
          </Badge>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>
          {t("roles.switchRole", "Switch Role")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {allRoles.map((role) => {
          const Icon = roleIcons[role as keyof typeof roleIcons];
          const isActive = role === activeRole;

          return (
            <DropdownMenuItem
              key={role}
              onClick={() => !isActive && switchRole(role as "client" | "cleaner" | "admin")}
              disabled={isActive || isLoading}
              className="flex items-center gap-2 cursor-pointer"
            >
              {Icon && <Icon className="h-4 w-4" />}
              <span className="flex-1">{getRoleLabel(role)}</span>
              {isActive && (
                <Badge
                  variant="secondary"
                  className={roleColors[role as keyof typeof roleColors]}
                >
                  {t("roles.active", "Active")}
                </Badge>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
